import type { FormFieldDef } from '@hcc/types';
import { MASKABLE_FIELD_TYPES, applyMask, maskPlaceholder } from '@hcc/types';
import { inputClass } from './fieldStyles';
import { FieldLabelText, FieldHelp } from './fieldChrome';
import { asString, type FieldValue } from './formValues';

interface TextFieldViewProps {
  field: FormFieldDef;
  value: FieldValue;
  onChange: (next: FieldValue) => void;
  disabled?: boolean;
  err: boolean;
}

/**
 * The free-text input family: long_text (textarea) and the single-line
 * short_text / email / phone / number types with optional input mask.
 */
export function TextFieldView({ field, value, onChange, disabled, err }: TextFieldViewProps) {
  if (field.type === 'long_text') {
    return (
      <label className="block text-sm">
        <FieldLabelText field={field} />
        <textarea
          rows={4}
          value={asString(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass(err)}
        />
        <FieldHelp field={field} />
      </label>
    );
  }

  // short_text / email / phone / number
  const mask = field.mask && MASKABLE_FIELD_TYPES.includes(field.type) ? field.mask : undefined;
  // A masked value can carry literals/spaces, so it must be a plain text box
  // (a number input would reject "12 34"); the keypad stays numeric for masks.
  const inputType = mask
    ? 'text'
    : field.type === 'email'
      ? 'email'
      : field.type === 'phone'
        ? 'tel'
        : field.type === 'number'
          ? 'number'
          : 'text';
  return (
    <label className="block text-sm">
      <FieldLabelText field={field} />
      <input
        type={inputType}
        inputMode={mask && field.type === 'number' ? 'numeric' : undefined}
        value={asString(value)}
        disabled={disabled}
        placeholder={mask ? maskPlaceholder(mask) : undefined}
        onChange={(e) => onChange(mask ? applyMask(mask, e.target.value) : e.target.value)}
        className={inputClass(err)}
      />
      <FieldHelp field={field} />
    </label>
  );
}
